import { Close, Delete, Label, Save } from '@mui/icons-material';
import {
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import React, { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { TagReadDTO, TagUpdateDTO } from '../../../api/dto';
import { tagActivity, untagActivity } from '../../../api/tags';
import { useApplicationContext } from '../../../components/application/ApplicationContextProvider';
import { ROUTES } from '../../../routes/routes';
import { FormGenerator } from '../../FormGenerator';
import { tagSchema } from '../../schemas/tag';
import { TFormElement } from '../../types';
import { UiSchemaType } from '../../UiSchemaType';

interface TaglistDialogProps {
    activityId: number | undefined;
    handleClose: () => void;
    open: boolean;
    refetchActivityTags: () => void;
    tags: TagReadDTO[] | undefined;
}

export const TaglistDialog: React.FC<TaglistDialogProps> = ({
    activityId,
    handleClose,
    open,
    refetchActivityTags,
    tags,
}) => {
    const navigate = useNavigate();
    const intl = useIntl();
    const { currentUser } = useApplicationContext();
    const form = useForm<{ newTags: { tag: TagUpdateDTO }[] }>({
        mode: 'onSubmit',
        defaultValues: { newTags: [] },
    });
    const formRootElement: TFormElement = useMemo(
        () => ({
            type: UiSchemaType.FIELD_ARRAY,
            path: 'newTags',
            inline: true,
            element: tagSchema({
                label: intl.formatMessage({ id: 'document.tags.newTag' }),
                path: 'tag',
                extraProps: {
                    activityId: activityId!,
                    refetchActivityTags: refetchActivityTags,
                },
            }),
            appendLabel: intl.formatMessage({ id: 'document.tags.addNewTag' }),
            removeLabel: intl.formatMessage({ id: 'document.tags.removeNewTag' }),
            singleElement: true,
        }),
        [intl]
    );

    useEffect(() => {
        if (open) {
            form.reset({ newTags: [] });
        }
    }, [open, form]);

    const { mutate: mutateUntagActivity, isLoading: isUntagging } = useMutation(
        async (data: { tagId: number }) => {
            if (activityId) {
                await untagActivity({ activityId: activityId, tagId: data.tagId });
                await refetchActivityTags();
            }
        },
        {
            onSuccess: () => {
                form.reset();
            },
            onError: () => toast.error(intl.formatMessage({ id: 'document.tags.error' })),
        }
    );

    const { mutate: mutateTagActivity, isLoading: isTagging } = useMutation(
        async (data: { tagName: string }) => {
            if (activityId) {
                await tagActivity({ id: activityId, tagName: data.tagName });
                await refetchActivityTags();
            }
        },
        {
            onSuccess: () => {
                form.reset();
            },
            onError: () => toast.error(intl.formatMessage({ id: 'document.tags.error' })),
        }
    );
    const submit = form.handleSubmit((data) => {
        mutateTagActivity({ tagName: data.newTags[0].tag.name });
    });
    const watchNewTags = form.watch('newTags');
    const canSubmit = watchNewTags.length > 0 && watchNewTags.every((a) => !!a && !!a.tag);
    const isLoading = isTagging || isUntagging;
    const groupedTags = tags?.reduce((acc, tag) => {
        const key = tag.source === 'NIM' ? tag.orgUnit?.code ?? 'OTHER' : 'OIM';
        if (!acc[key]) {
            acc[key] = [];
        }
        acc[key].push(tag);
        return acc;
    }, {} as Record<string, typeof tags>);

    const sortedGroups = Object.entries(groupedTags ?? {}).sort(([a], [b]) => {
        if (a === 'OIM') return 1;
        if (b === 'OIM') return -1;
        return a.length - b.length;
    });

    const isTagReadOnly = (tag: TagReadDTO) => {
        const userOrgUnitCode = currentUser.orgUnit?.code;
        const tagOrgUnitCode = tag.orgUnit?.code;
        if (!userOrgUnitCode || !tagOrgUnitCode) return true;
        if (userOrgUnitCode === tagOrgUnitCode) return false;

        const userParts = userOrgUnitCode.split('-');
        const tagParts = tagOrgUnitCode.split('-');

        if (tagParts.length > userParts.length) return true;

        return !tagParts.every((part, i) => part === userParts[i]);
    };

    return (
        <Dialog onClose={!isLoading ? handleClose : undefined} open={open} fullWidth maxWidth="sm">
            <DialogTitle>
                <FormattedMessage id={'document.tags.manageTags'} />
                <IconButton
                    onClick={handleClose}
                    disabled={isLoading}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {isLoading ? (
                    <LinearProgress />
                ) : (
                    <Stack direction="column" spacing={2}>
                        <List dense>
                            {sortedGroups.map(([group, groupTags]) => (
                                <Box key={group} sx={{ mb: 1 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            px: 2,
                                            py: 0.5,
                                            display: 'block',
                                            fontWeight: 600,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {group}
                                    </Typography>

                                    {groupTags.map((tag) => {
                                        const readonly = isTagReadOnly(tag);
                                        return (
                                            <ListItem key={tag.id}>
                                                <ListItemIcon>
                                                    <Label />
                                                </ListItemIcon>
                                                <ListItemText
                                                    primary={
                                                        <Tooltip
                                                            title={intl.formatMessage({
                                                                id: 'document.tags.showActivitiesWithTag',
                                                            })}
                                                            arrow
                                                        >
                                                            <Typography
                                                                color={tag.source === 'NIM' ? 'black' : 'grey'}
                                                                variant="body2"
                                                                sx={{ cursor: 'pointer' }}
                                                                onClick={() => {
                                                                    navigate(ROUTES.activity.search, {
                                                                        state: {
                                                                            filters: [
                                                                                {
                                                                                    field: 'activityTags.tag.id',
                                                                                    operator: 'is',
                                                                                    value: tag,
                                                                                },
                                                                            ],
                                                                        },
                                                                    });
                                                                }}
                                                            >
                                                                {tag.name}
                                                            </Typography>
                                                        </Tooltip>
                                                    }
                                                />
                                                {tag.source === 'NIM' && (
                                                    <Tooltip
                                                        title={
                                                            readonly
                                                                ? intl.formatMessage({
                                                                      id: 'document.tags.noPermissionToRemove',
                                                                  })
                                                                : intl.formatMessage({ id: 'document.tags.removeTag' })
                                                        }
                                                    >
                                                        <span>
                                                            <IconButton
                                                                color="error"
                                                                disabled={readonly}
                                                                onClick={() => mutateUntagActivity({ tagId: tag.id })}
                                                            >
                                                                <Delete />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                )}
                                            </ListItem>
                                        );
                                    })}
                                </Box>
                            ))}
                        </List>
                        {!!mutateTagActivity && (
                            <FormProvider {...form}>
                                <FormGenerator rootElement={formRootElement} form={form} disabled={isLoading} />
                                {watchNewTags.length > 0 && (
                                    <>
                                        <Button
                                            variant="contained"
                                            startIcon={<Save />}
                                            onClick={submit}
                                            disabled={isLoading || !canSubmit}
                                        >
                                            <FormattedMessage id="document.action.save" />
                                        </Button>
                                    </>
                                )}
                            </FormProvider>
                        )}
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};
